import './App.css';
import './Utils/shared.scss';
import './Utils/text.scss';

import { ErrorBoundary } from '@sentry/react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';

import { ChecklistSubmissionsListDrawer } from 'src/Components/Drawers/ChecklistSubmissionsListDrawer';
import { CreateBankAccountDrawer } from 'src/Components/Drawers/CreateBankAccountDrawer';
import { CreateExpenseDrawer } from 'src/Components/Drawers/CreateExpenseDrawer';
import { CreateInstructionDrawer } from 'src/Components/Drawers/CreateInstructionDrawer';
import { CreateMemoryDrawer } from 'src/Components/Drawers/CreateMemoryDrawer';
import CreateMoneyTransferDrawer from 'src/Components/Drawers/CreateMoneyTransferDrawer';
import { CreatePracticalInformationDrawer } from 'src/Components/Drawers/CreatePracticalInformationDrawer';
import { DayDetailDrawer } from 'src/Components/Drawers/DayDetailDrawer';
import { EditBankAccountDrawer } from 'src/Components/Drawers/EditBankAccountDrawer';
import { EditChecklistDrawer } from 'src/Components/Drawers/EditChecklistDrawer';
import { EditContactDrawer } from 'src/Components/Drawers/EditContactDrawer';
import { EditEventDrawer } from 'src/Components/Drawers/EditEventDrawer';
import { EditExpenseDrawer } from 'src/Components/Drawers/EditExpenseDrawer';
import { EditInstructionDrawer } from 'src/Components/Drawers/EditInstructionDrawer';
import { EditInventoryItemDrawer } from 'src/Components/Drawers/EditInventoryItemDrawer';
import { EditMemoryDrawer } from 'src/Components/Drawers/EditMemoryDrawer';
import { EditMoneyTransferDrawer } from 'src/Components/Drawers/EditMoneyTransferDrawer';
import { EditPracticalInformationDrawer } from 'src/Components/Drawers/EditPracticalInformationDrawer';
import { HouseholdFormDrawer } from 'src/Components/Drawers/HouseholdFormDrawer';
import { SubmitChecklistDrawer } from 'src/Components/Drawers/SubmitChecklistDrawer';
import { ViewBankAccountDrawer } from 'src/Components/Drawers/ViewBankAccountDrawer';
import { ViewDocumentDrawer } from 'src/Components/Drawers/ViewDocumentDrawer';
import { ViewEventDrawer } from 'src/Components/Drawers/ViewEventDrawer';
import { ViewExpenseDrawer } from 'src/Components/Drawers/ViewExpenseDrawer';
import { ViewExportedContactDrawer } from 'src/Components/Drawers/ViewExportedContactDrawer';
import { ViewExportedEventDrawer } from 'src/Components/Drawers/ViewExportedEventDrawer';
import { ViewInstructionDrawer } from 'src/Components/Drawers/ViewInstructionDrawer';
import { ViewInventoryItemDrawer } from 'src/Components/Drawers/ViewInventoryItemDrawer';
import { ViewMemoryDrawer } from 'src/Components/Drawers/ViewMemoryDrawer';
import { ViewPracticalInformationDrawer } from 'src/Components/Drawers/ViewPracticalInformationDrawer';
import { FeaturePreview } from 'src/Components/FeaturePreview';
import { NotReadyYet } from 'src/Components/NotReadyYet';
import { AdminPage } from 'src/Containers/Admin/AdminPage';
import { EmailVerificationPage } from 'src/Containers/Auth/EmailVerificationPage';
import { RegisterPage } from 'src/Containers/Auth/RegisterPage';
import { ResetPasswordPage } from 'src/Containers/Auth/ResetPasswordPage';
import { DocumentsPage } from 'src/Containers/Documents/DocumentsPage';
import { ExpensesMainPage } from 'src/Containers/Expenses/ExpensesMainPage';
import { ExportPage } from 'src/Containers/Exports/ExportPage';
import { ExportSubscribePage } from 'src/Containers/Exports/ExportSubscribePage';
import FundCallDetailsPage from 'src/Containers/FundCalls/FundCallDetailsPage';
import FundCallWizardPage from 'src/Containers/FundCalls/FundCallWizardPage';
import MyStatementPage from 'src/Containers/FundCalls/MyStatementPage';
import { InfosMainPage } from 'src/Containers/Infos/InfosMainPage';
import { PracticalnformationMainPage } from 'src/Containers/Infos/PracticalnformationMainPage';
import { InfosAndContactsMainPage } from 'src/Containers/InfosAndContacts/InfosAndContactsMainPage';
import { InstallPage } from 'src/Containers/Install/InstallPage';
import { ChecklistsMainPage } from 'src/Containers/Instructions/ChecklistsMainPage';
import { GuidesMainPage } from 'src/Containers/Instructions/GuidesMainPage';
import { InstructionsMainPage } from 'src/Containers/Instructions/InstructionsMainPage';
import { InternalBackofficePage } from 'src/Containers/Internal/InternalBackofficePage';
import { InvitationPage } from 'src/Containers/Invitations/InvitationPage';
import { JoinByCodePage } from 'src/Containers/Invitations/JoinByCodePage';
import { AcceptInvitationOrCreateAssetPage } from 'src/Containers/Onboarding/AcceptInvitationOrCreateAssetPage';
import { OnboardingPage } from 'src/Containers/Onboarding/OnboardingPage';
import { RequireOnboarding } from 'src/Containers/Onboarding/RequireOnboarding';
import { PlanningPage } from 'src/Containers/Planning/PlanningPage';
import { ProfileMainPage } from 'src/Containers/Profile/ProfileMainPage';
import { useMixpanelPageTracking } from 'src/Hooks/Tracking/useMixpanelPageTracking';
import { useUtmTracker } from 'src/Hooks/Tracking/useUtmTracker';
import { InstallPwaProvider } from 'src/Providers/InstallPwaProvider';
import { PendingInvitationsModalProvider } from 'src/Providers/PendingInvitationsModalProvider';
import { PushNotificationProvider } from 'src/Providers/PushNotificationProvider';
import { setupConsentAwareServices } from 'src/Services/Hubspot';
import { trackErrorEncountered, trackNotificationClicked } from 'src/Services/Mixpanel/events';

import { RequireInternalAdmin } from './Components/Auth/RequireInternalAdmin';
import { RequireProfileCompletion } from './Components/Auth/RequireProfileCompletion';
import RequireTermsAccept from './Components/Auth/RequireTermsAccept/RequireTermsAccept.component';
import { ThirdPartySignInHandler } from './Components/Auth/ThirdPartySignInHandler';
import { CreateChecklistDrawer } from './Components/Drawers/CreateChecklistDrawer';
import { CreateContactDrawer } from './Components/Drawers/CreateContactDrawer';
import { CreateEventDrawer } from './Components/Drawers/CreateEventDrawer';
import { CreateIdeaDrawer } from './Components/Drawers/CreateIdeaDrawer';
import { CreateInventoryItemDrawer } from './Components/Drawers/CreateInventoryItemDrawer';
import { CreateProjectDrawer } from './Components/Drawers/CreateProjectDrawer';
import { CreateTaskDrawer } from './Components/Drawers/CreateTaskDrawer';
import { EditProjectDrawer } from './Components/Drawers/EditProjectDrawer';
import { EditTaskDrawer } from './Components/Drawers/EditTaskDrawer';
import { PreviewProjectDrawer } from './Components/Drawers/PreviewProjectDrawer';
import { ViewContactDrawer } from './Components/Drawers/ViewContactDrawer';
import { ViewTaskDrawer } from './Components/Drawers/ViewTaskDrawer';
import AcceptTermsForm from './Components/Forms/AcceptTermsForm/AcceptTermsForm.component';
import MatomoTagManager from './Components/Shared/MatomoTagManager/MatomoTagManager.component';
import SignInPage from './Containers/Auth/SignInPage';
import { CommunityHome } from './Containers/Community/CommunityHome';
import { CommunityFeedItemDetailPage } from './Containers/Community/CommunityHome/CommunityFeed/CommunityFeedItemDetailPage';
import { RedirectToAssetCommunity } from './Containers/Community/RedirectToAssetCommunity';
import { ContactsListPage } from './Containers/Contacts/ContactsListPage';
import { DashboardMainPage } from './Containers/Dashboard/DashboardMainPage';
import { InventoryExitedPage } from './Containers/Inventory/InventoryExitedPage';
import { InventoryPage } from './Containers/Inventory/InventoryPage';
import MainLayout from './Containers/MainLayout';
import { RedirectToFirstAsset } from './Containers/MainLayout/RedirectToFirstAsset';
import { MemoriesTimelinePage } from './Containers/Memories/MemoriesTimelinePage';
import { ProjectsMainPage } from './Containers/Projects/ProjectsMainPage';
import { TasksMainPage } from './Containers/Tasks/TasksMainPage';
import { supertokensInit } from './Services/Supertokens';

import './Services/Dates';

supertokensInit();

const ErrorFallback = () => {
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Oups, une erreur est survenue.</h2>
      <p>Notre équipe a été prévenue. Vous pouvez essayer de rafraîchir la page.</p>
    </div>
  );
};

const LegacyInfosRedirect = () => {
  const { assetId, '*': rest } = useParams();
  const tail = rest ? `/${rest}` : '';
  return <Navigate to={`/asset/${assetId}/infos-et-contacts/infos${tail}`} replace />;
};

const LegacyContactsRedirect = () => {
  const { assetId, '*': rest } = useParams();
  const tail = rest ? `/${rest}` : '';
  return <Navigate to={`/asset/${assetId}/infos-et-contacts/contacts${tail}`} replace />;
};

function App() {
  useUtmTracker();
  useMixpanelPageTracking();
  const navigate = useNavigate();

  useEffect(() => {
    setupConsentAwareServices();

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'navigate') {
        trackNotificationClicked({ url: event.data.url });
        navigate(event.data.url);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [navigate]);

  return (
    <>
      <ErrorBoundary
        fallback={<ErrorFallback />}
        onError={error => {
          console.error(error);
          trackErrorEncountered({
            error_message: error instanceof Error ? error.message : String(error),
            page_context: window.location.pathname,
          });
        }}
      >
        <MatomoTagManager />
        <InstallPwaProvider>
          <PendingInvitationsModalProvider>
            <Routes>
              <Route path="/export/:token/abonnement" element={<ExportSubscribePage />} />
              <Route path="/export/:token" element={<ExportPage />}>
                <Route path="contact/:contactId" element={<ViewExportedContactDrawer />} />
                <Route path="event/:eventId" element={<ViewExportedEventDrawer />} />
                {/* practical-information exports are handled by ExportPage via ExportWithEntitiesDtoEntityTypeEnum */}
              </Route>
              <Route
                path="/"
                element={
                  <SessionAuth>
                    <RequireTermsAccept>
                      <RequireOnboarding>
                        <Navigate to={'/asset'} />
                      </RequireOnboarding>
                    </RequireTermsAccept>
                  </SessionAuth>
                }
              />
              <Route
                path="/asset/new"
                element={
                  <SessionAuth>
                    <RequireTermsAccept>
                      <RequireOnboarding>
                        <OnboardingPage isFirstAsset={false} />
                      </RequireOnboarding>
                    </RequireTermsAccept>
                  </SessionAuth>
                }
              />
              <Route
                path="/profile"
                element={
                  <SessionAuth>
                    <RequireTermsAccept>
                      <RequireProfileCompletion>
                        <ProfileMainPage />
                      </RequireProfileCompletion>
                    </RequireTermsAccept>
                  </SessionAuth>
                }
              />
              <Route
                path="/internal"
                element={
                  <SessionAuth>
                    <RequireTermsAccept>
                      <RequireInternalAdmin>
                        <InternalBackofficePage />
                      </RequireInternalAdmin>
                    </RequireTermsAccept>
                  </SessionAuth>
                }
              />
              <Route path="/asset" element={<RedirectToFirstAsset />} />
              <Route
                path="/asset/:assetId"
                element={
                  <SessionAuth>
                    <RequireTermsAccept>
                      <PushNotificationProvider>
                        <RequireProfileCompletion>
                          <MainLayout />
                        </RequireProfileCompletion>
                      </PushNotificationProvider>
                    </RequireTermsAccept>
                  </SessionAuth>
                }
              >
                <Route index element={<Navigate to="dashboard" />} />
                <Route path="dashboard" element={<DashboardMainPage />} />
                <Route path="planning" element={<PlanningPage />}>
                  <Route path="new-event" element={<CreateEventDrawer />} />
                  <Route path="day/:date" element={<DayDetailDrawer />} />
                  <Route path="event/:eventId" element={<ViewEventDrawer />} />
                  <Route path="event/:eventId/edit" element={<EditEventDrawer />} />
                  <Route
                    path="practical-information/:practicalInformationId"
                    element={<ViewPracticalInformationDrawer />}
                  />
                  <Route
                    path="practical-information/:practicalInformationId/edit"
                    element={<EditPracticalInformationDrawer />}
                  />
                  <Route path="project/:projectId" element={<PreviewProjectDrawer />} />
                  <Route path="project/:projectId/edit" element={<EditProjectDrawer />} />
                  <Route path="task/:taskId" element={<ViewTaskDrawer />} />
                  <Route path="task/:taskId/edit" element={<EditTaskDrawer />} />
                </Route>
                <Route path="documents" element={<DocumentsPage />}>
                  <Route path=":fileId" element={<ViewDocumentDrawer />} />
                </Route>
                <Route path="infos-et-contacts" element={<InfosAndContactsMainPage />}>
                  <Route index element={<Navigate to="contacts" replace />} />
                  <Route path="infos" element={<InfosMainPage />}>
                    <Route index element={<Navigate to="practical-info" replace />} />
                    <Route path="practical-info" element={<PracticalnformationMainPage />}>
                      <Route path="new" element={<CreatePracticalInformationDrawer />} />
                      <Route
                        path=":practicalInformationId"
                        element={<ViewPracticalInformationDrawer />}
                      />
                      <Route
                        path=":practicalInformationId/edit"
                        element={<EditPracticalInformationDrawer />}
                      />
                    </Route>
                  </Route>
                  <Route path="contacts" element={<ContactsListPage />}>
                    <Route path="new" element={<CreateContactDrawer />} />
                    <Route path=":contactId" element={<ViewContactDrawer />} />
                    <Route path=":contactId/edit" element={<EditContactDrawer />} />
                  </Route>
                </Route>
                <Route path="infos/*" element={<LegacyInfosRedirect />} />
                <Route path="contacts/*" element={<LegacyContactsRedirect />} />
                <Route path="guides" element={<GuidesMainPage />}>
                  <Route index element={<Navigate to="instructions" />} />
                  <Route path="instructions" element={<InstructionsMainPage />}>
                    <Route path="new" element={<CreateInstructionDrawer />} />
                    <Route path=":instructionId" element={<ViewInstructionDrawer />} />
                    <Route path=":instructionId/edit" element={<EditInstructionDrawer />} />
                  </Route>
                  <Route path="checklists" element={<ChecklistsMainPage />}>
                    <Route path="new" element={<CreateChecklistDrawer />} />
                    <Route path=":checklistId/edit" element={<EditChecklistDrawer />} />
                    <Route path=":checklistId/submit" element={<SubmitChecklistDrawer />} />
                    <Route
                      path=":checklistId/submissions"
                      element={<ChecklistSubmissionsListDrawer />}
                    />
                  </Route>
                </Route>
                <Route path="expenses" element={<ExpensesMainPage />}>
                  <Route path="new" element={<CreateExpenseDrawer />} />
                  <Route path="new-transfer" element={<CreateMoneyTransferDrawer />} />
                  <Route path="fund-calls/new" element={<FundCallWizardPage />} />
                  <Route path="fund-calls/:fundCallId/edit" element={<FundCallWizardPage />} />
                  <Route path="fund-calls/:fundCallId" element={<FundCallDetailsPage />} />
                  <Route path="my-statement" element={<MyStatementPage />} />
                  <Route path=":transferId/edit-transfer" element={<EditMoneyTransferDrawer />} />
                  <Route path=":expenseId" element={<ViewExpenseDrawer />} />
                  <Route path=":expenseId/edit" element={<EditExpenseDrawer />} />
                  <Route path="new-bank-account" element={<CreateBankAccountDrawer />} />
                  <Route path="bank-account/:bankAccountId" element={<ViewBankAccountDrawer />} />
                  <Route
                    path="bank-account/:bankAccountId/edit"
                    element={<EditBankAccountDrawer />}
                  />
                </Route>
                <Route path="community" element={<CommunityHome />} />
                <Route path="community/post/:postId" element={<CommunityFeedItemDetailPage />} />
                <Route path="memories" element={<MemoriesTimelinePage />}>
                  <Route path="new" element={<CreateMemoryDrawer />} />
                  <Route path=":memoryId" element={<ViewMemoryDrawer />} />
                  <Route path=":memoryId/edit" element={<EditMemoryDrawer />} />
                </Route>
                <Route
                  path="community/post/:postId/comment/:commentId"
                  element={<CommunityFeedItemDetailPage />}
                />
                <Route path="admin" element={<AdminPage />}>
                  <Route path="foyers/new" element={<HouseholdFormDrawer />} />
                  <Route path="foyers/:householdId/edit" element={<HouseholdFormDrawer />} />
                </Route>
                <Route path="maintenance" element={<NotReadyYet />} />
                <Route path="project-and-works" element={<ProjectsMainPage />}>
                  <Route path="new" element={<CreateProjectDrawer />} />
                  <Route path="new-idea" element={<CreateIdeaDrawer />} />
                  <Route path=":projectId" element={<PreviewProjectDrawer />} />
                  <Route path=":projectId/edit" element={<EditProjectDrawer />} />
                </Route>
                <Route path="tasks" element={<TasksMainPage />}>
                  <Route path="new" element={<CreateTaskDrawer />} />
                  <Route path=":taskId" element={<ViewTaskDrawer />} />
                  <Route path=":taskId/edit" element={<EditTaskDrawer />} />
                </Route>
                <Route
                  path="incidents-and-claims"
                  element={<FeaturePreview moduleKey="incidents" />}
                />
                <Route path="meetings" element={<FeaturePreview moduleKey="meetings" />} />
                {/* Same routed drawers as the other entities: /new, /:itemId and /:itemId/edit
                    keep the links of the former pages working. */}
                <Route path="inventories" element={<InventoryPage />}>
                  <Route path="new" element={<CreateInventoryItemDrawer />} />
                  <Route path=":itemId" element={<ViewInventoryItemDrawer />} />
                  <Route path=":itemId/edit" element={<EditInventoryItemDrawer />} />
                </Route>
                <Route path="inventories/exited" element={<InventoryExitedPage />}>
                  <Route path=":itemId" element={<ViewInventoryItemDrawer />} />
                  <Route path=":itemId/edit" element={<EditInventoryItemDrawer />} />
                </Route>
                <Route path="resources" element={<FeaturePreview moduleKey="resources" />} />
                <Route path="templates" element={<NotReadyYet />} />
              </Route>
              {/* « Entre nous » vit désormais sous /asset/:assetId/community. Ces deux
                  redirections gardent vivants les anciens liens et les notifications
                  push, qui pointent sur /community et /community/post/:id. */}
              <Route path="/community" element={<RedirectToAssetCommunity />} />
              <Route path="/community/post/:postId" element={<RedirectToAssetCommunity />} />
              <Route
                path="/community/post/:postId/comment/:commentId"
                element={<RedirectToAssetCommunity />}
              />
              <Route path="/invitations/accept/:token" element={<InvitationPage />} />
              <Route path="/join" element={<JoinByCodePage />} />
              <Route path="/auth" element={<SignInPage />} />
              <Route path="/auth/verify-email" element={<EmailVerificationPage />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<ThirdPartySignInHandler />} />
              <Route path="/onboarding" element={<AcceptInvitationOrCreateAssetPage />} />

              <Route path="/accept-terms" element={<AcceptTermsForm />} />
              <Route path="/install" element={<InstallPage />} />
            </Routes>
          </PendingInvitationsModalProvider>
        </InstallPwaProvider>
      </ErrorBoundary>
    </>
  );
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(() => {})
      .catch(error => {
        console.log('Error while registering the Service Worker :', error);
      });
  });
}

export default App;
